/**
 * PanelResizeHandle
 *
 * The drag handle for the three panels that draw their own divider rather than
 * letting antd's `Drawer resizable` do it: the agent panel, the mapping review
 * panel and the records browser split. It holds no state — width in `uiState`,
 * drag origin in a ref, highlight in the `.panel-resize-handle` CSS fed by the
 * caller's `--panel-grip-*` properties. The hook this replaced kept a hover flag
 * in React state, so every panel reading it re-rendered when any handle was
 * merely pointed at; a shared component is safe where that hook was not, because
 * there is no state here for two panels to share.
 */

import { CSSProperties, FC, KeyboardEvent, PointerEvent, useRef } from 'react';
import { useSnapshot } from 'valtio';

import {
  clampPanelWidth,
  panelMaxWidth,
  panelMinWidth,
  panelStep,
  setPanelWidth,
  uiState,
  type PanelKey,
} from '../../store/uiState';

/**
 * Placement plus the grip's colours, which come from antd tokens at the call site.
 * The two colours are required because the CSS has no sensible default for them;
 * the geometry variables fall back to the values in `.panel-resize-handle-grip`.
 */
type PanelHandleStyle = CSSProperties & {
  '--panel-grip-color': string;
  '--panel-grip-color-active': string;
  '--panel-grip-width'?: string;
  '--panel-grip-height'?: string;
  '--panel-grip-radius'?: string;
  /** Track background while dragging. Defaults to transparent. */
  '--panel-track-active'?: string;
};

type PanelResizeHandleProps = {
  panel: PanelKey;
  /** Which viewport side the panel is anchored to — sets the drag direction. */
  anchor: 'left' | 'right';
  /** Accessible name, unique on the page. */
  label: string;
  style: PanelHandleStyle;
};

const PanelResizeHandle: FC<PanelResizeHandleProps> = ({ panel, anchor, label, style }) => {
  const { width } = useSnapshot(uiState[panel], { sync: true });
  const dragStart = useRef<{ pointerX: number; width: number } | null>(null);

  const draggedWidth = (start: { pointerX: number; width: number }, pointerX: number): number => {
    const delta = pointerX - start.pointerX;
    return anchor === 'right' ? start.width - delta : start.width + delta;
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = { pointerX: event.clientX, width: uiState[panel].width };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const start = dragStart.current;
    if (!start) return;
    setPanelWidth(panel, draggedWidth(start, event.clientX), { persist: false });
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>): void => {
    const start = dragStart.current;
    if (!start) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    dragStart.current = null;
    setPanelWidth(panel, draggedWidth(start, event.clientX));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = panelStep(panel);
    const current = uiState[panel].width;
    const movingLeft = anchor === 'right' ? current + step : current - step;
    const movingRight = anchor === 'right' ? current - step : current + step;
    const apply = (next: number): void => {
      event.preventDefault();
      setPanelWidth(panel, next);
    };
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        return apply(movingLeft);
      case 'ArrowRight':
      case 'ArrowDown':
        return apply(movingRight);
      case 'Home':
        return apply(panelMinWidth(panel));
      case 'End':
        return apply(panelMaxWidth(panel));
    }
  };

  return (
    <div
      className="panel-resize-handle"
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation="vertical"
      aria-valuenow={Math.round(clampPanelWidth(panel, width))}
      aria-valuemin={Math.round(panelMinWidth(panel))}
      aria-valuemax={Math.round(panelMaxWidth(panel))}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
    >
      <span className="panel-resize-handle-grip" aria-hidden />
    </div>
  );
};

export default PanelResizeHandle;
export type { PanelHandleStyle };
